'use client';

/**
 * OAuth回调处理页面
 * 
 * 处理Google OAuth登录成功后的令牌
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AuthService } from '../../shared/api/authService';

export default function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithUser } = useAuth();
  
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('正在处理OAuth登录...');
  const [hasProcessed, setHasProcessed] = useState(false);

  useEffect(() => {
    // 防止重复处理
    if (hasProcessed) {
      return;
    }

    const handleOAuthCallback = async () => {
      try {
        setHasProcessed(true);
        
        // 从URL参数中获取令牌
        const token = searchParams.get('token');
        const refreshToken = searchParams.get('refresh_token');
        const error = searchParams.get('error');
        
        if (error) {
          throw new Error(decodeURIComponent(error));
        }
        
        if (!token || !refreshToken) {
          throw new Error('未收到有效的认证令牌');
        }
        
        // 保存令牌
        AuthService.saveTokens({
          access_token: token,
          refresh_token: refreshToken,
          expires_in: 3600,
          token_type: 'Bearer'
        });
        
        // 获取用户信息并更新认证状态
        const userProfile = await AuthService.getCurrentUser();
        loginWithUser(userProfile);
        
        setStatus('success');
        setMessage('OAuth登录成功！正在跳转...');
        
        // 跳转到returnUrl或首页
        const returnUrl = searchParams.get('returnUrl');
        setTimeout(() => {
          navigate(returnUrl || '/');
        }, 1500);
        
      } catch (error) {
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'OAuth登录失败');
        
        // 3秒后跳转到登录页面
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate, hasProcessed, loginWithUser]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 p-8 max-w-md w-full mx-4">
        <div className="text-center">
          {/* 状态图标 */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6">
            {status === 'processing' && (
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            )}
            {status === 'success' && (
              <div className="bg-green-100 rounded-full h-16 w-16 flex items-center justify-center">
                <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
            )}
            {status === 'error' && (
              <div className="bg-red-100 rounded-full h-16 w-16 flex items-center justify-center">
                <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </div>
            )}
          </div>
          
          {/* 状态标题 */}
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {status === 'processing' && 'OAuth登录处理中'}
            {status === 'success' && '登录成功！'}
            {status === 'error' && '登录失败'}
          </h2>
          
          {/* 状态消息 */}
          <p className="text-gray-600 mb-6">
            {message}
          </p>
          
          {/* 进度指示器 */}
          {status === 'processing' && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
            </div>
          )}
          
          {/* 错误时的重试按钮 */}
          {status === 'error' && (
            <div className="space-y-3">
              <button
                onClick={() => navigate('/login')}
                className="w-full px-4 py-3 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-200"
              >
                返回登录页面
              </button>
              <button
                onClick={() => window.location.reload()}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                重试
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 